// src/ShowcaseContest.js
import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';

const db = getFirestore();

const JUDGES = ['Street Flavor', 'Struggle Jennings', 'Drummaboy'];

const CONTESTANTS = [
  'Kidd Pittman',
  'Fooly',
  'Supreme Lyrik',
  'Matmiie',
  'O.N.E',
  'Blvck Wizzle',
  'Austin aGhawk Hawkins',
  'Tony Carpenter',
  'JaydeefrmtheV',
  'Ramon Norman jr',
];

const CRITERIA = [
  {
    key: 'delivery',
    label: 'Delivery',
    description: '(Clarity, breath control)',
  },
  {
    key: 'originality',
    label: 'Originality',
    description: '(Unique sound?)',
  },
  {
    key: 'content',
    label: 'Content',
    description: '(Message or concept delivered?)',
  },
  {
    key: 'productionQuality',
    label: 'Production Quality',
    description: '(Performance mix, audio quality)',
  },
  {
    key: 'marketability',
    label: 'Marketability',
    description: '(Could you hear this on a major platform?)',
  },
];

function computeRanking(judgeData) {
  // judgeData: { [judgeName]: { [artistName]: { criterionKey: score, ... } } }
  return CONTESTANTS.map((name) => {
    let total = 0;
    JUDGES.forEach((judge) => {
      const artistScores = judgeData[judge]?.[name] || {};
      CRITERIA.forEach((crit) => {
        const v = artistScores[crit.key];
        if (typeof v === 'number') {
          total += v;
        }
      });
    });
    return { name, totalScore: total };
  }).sort((a, b) => b.totalScore - a.totalScore);
}

const ShowcaseContest = () => {
  const [selectedJudge, setSelectedJudge] = useState(null);
  const [view, setView] = useState('selectJudge'); // 'selectJudge' | 'list' | 'detail'
  const [judgeData, setJudgeData] = useState({}); // judgeName -> { artistName -> scores }
  const [rankedContestants, setRankedContestants] = useState([]);
  const [activeContestant, setActiveContestant] = useState(null);
  const [localScores, setLocalScores] = useState({});
  const [comment, setComment] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load all judges' documents whenever we land on the contestants list view
  useEffect(() => {
    const loadAllJudgeDocs = async () => {
      if (!selectedJudge || view !== 'list') return;
      setLoadingList(true);
      try {
        const newJudgeData = {};
        for (const judge of JUDGES) {
          const ref = doc(db, 'ShowcaseContest', judge);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const data = snap.data() || {};
            newJudgeData[judge] = data.scores || {};
          } else {
            newJudgeData[judge] = {};
          }
        }
        setJudgeData(newJudgeData);
        setRankedContestants(computeRanking(newJudgeData));
      } catch (err) {
        console.error('Error loading judge docs:', err);
      } finally {
        setLoadingList(false);
      }
    };

    loadAllJudgeDocs();
  }, [selectedJudge, view]);

  const handleSelectJudge = (judge) => {
    setSelectedJudge(judge);
    setView('list');
  };

  const openContestantDetail = (name) => {
    setActiveContestant(name);
    const currentJudgeScores = judgeData[selectedJudge]?.[name] || {};
    const initialScores = {};
    CRITERIA.forEach((crit) => {
      const v = currentJudgeScores[crit.key];
      initialScores[crit.key] = typeof v === 'number' ? v : null;
    });
    setLocalScores(initialScores);
    setComment(currentJudgeScores.comments || '');
    setView('detail');
  };

  const handleScoreChange = (criterionKey, value) => {
    if (!selectedJudge || !activeContestant) return;

    setLocalScores((prev) => ({
      ...prev,
      [criterionKey]: value,
    }));

    // Update local judgeData + ranking for instant UI update
    setJudgeData((prev) => {
      const updated = { ...prev };
      const judgeMap = { ...(updated[selectedJudge] || {}) };
      const contestantScores = { ...(judgeMap[activeContestant] || {}) };

      contestantScores[criterionKey] = value;
      contestantScores.artistName = activeContestant;

      judgeMap[activeContestant] = contestantScores;
      updated[selectedJudge] = judgeMap;

      setRankedContestants(computeRanking(updated));
      return updated;
    });

    // Persist to Firestore (creates doc if needed)
    const saveScore = async () => {
      try {
        const judgeRef = doc(db, 'ShowcaseContest', selectedJudge);
        await setDoc(
          judgeRef,
          {
            judgeName: selectedJudge,
            scores: {
              [activeContestant]: {
                artistName: activeContestant,
                [criterionKey]: value,
              },
            },
          },
          { merge: true }
        );
      } catch (err) {
        console.error('Error saving score:', err);
      }
    };

    saveScore();
  };

  const handleSave = async () => {
    if (!selectedJudge || !activeContestant) return;

    // Validate that all criteria have a score before saving
    const missingCriteria = CRITERIA.filter(
      (crit) => typeof localScores[crit.key] !== 'number'
    );

    if (missingCriteria.length > 0) {
      const missingLabels = missingCriteria.map((crit) => crit.label).join(', ');
      alert(
        `Please add a score for the following before saving:\n${missingLabels}`
      );
      return;
    }

    setSaving(true);
    try {
      const judgeRef = doc(db, 'ShowcaseContest', selectedJudge);
      await setDoc(
        judgeRef,
        {
          judgeName: selectedJudge,
          scores: {
            [activeContestant]: {
              artistName: activeContestant,
              comments: comment,
              ...localScores,
            },
          },
        },
        { merge: true }
      );

      // Update local cache with comment
      setJudgeData((prev) => {
        const updated = { ...prev };
        const judgeMap = { ...(updated[selectedJudge] || {}) };
        const contestantScores = { ...(judgeMap[activeContestant] || {}) };

        contestantScores.comments = comment;
        contestantScores.artistName = activeContestant;
        CRITERIA.forEach((crit) => {
          if (typeof localScores[crit.key] === 'number') {
            contestantScores[crit.key] = localScores[crit.key];
          }
        });

        judgeMap[activeContestant] = contestantScores;
        updated[selectedJudge] = judgeMap;
        setRankedContestants(computeRanking(updated));
        return updated;
      });

      // Go back to list only if all criteria were scored (enforced above)
      setView('list');
      setActiveContestant(null);
    } catch (err) {
      console.error('Error saving comment:', err);
      alert('Error saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleBackToList = () => {
    setView('list');
    setActiveContestant(null);
  };

  const renderJudgeSelect = () => (
    <div className="judge-select-container">
      <h1>Showcase Judging</h1>
      <p className="judge-select-subtitle">
        Select your judge profile to begin scoring each artist.
      </p>
      <div className="judge-cards">
        {JUDGES.map((judge) => (
          <button
            key={judge}
            className="judge-card"
            onClick={() => handleSelectJudge(judge)}
          >
            <div className="judge-card-name">{judge}</div>
            <div className="judge-card-tag">Tap to begin</div>
          </button>
        ))}
      </div>
      <p className="judge-warning">
        This page is for official judges only. Please do not share this link.
      </p>
    </div>
  );

  const renderContestantList = () => (
    <div className="contestant-list-container">
      <div className="top-bar">
        <button className="back-link" onClick={() => setView('selectJudge')}>
          ← Change Judge
        </button>
        <div className="current-judge-label">
          Current Judge:&nbsp;
          <span>{selectedJudge}</span>
        </div>
      </div>
      <h1>Artist Rankings</h1>
      <p className="judge-select-subtitle">
        Artists are ranked by total score across all judges. Tap an artist to
        enter or edit your scores.
      </p>

      {loadingList ? (
        <div className="loading-text">Loading scores…</div>
      ) : (
        <div className="contestant-list">
          {rankedContestants.map((item, index) => {
            const judgeScores =
              judgeData[selectedJudge]?.[item.name] || {};
            let judgeTotal = 0;
            CRITERIA.forEach((crit) => {
              const v = judgeScores[crit.key];
              if (typeof v === 'number') judgeTotal += v;
            });

            return (
              <button
                key={item.name}
                className="contestant-row"
                onClick={() => openContestantDetail(item.name)}
              >
                <div className="contestant-rank">{index + 1}</div>
                <div className="contestant-main">
                  <div className="contestant-name">{item.name}</div>
                  <div className="contestant-sub">
                    <span>Total (All Judges): {item.totalScore}</span>
                    <span className="dot-separator">•</span>
                    <span>Your Score: {judgeTotal}</span>
                  </div>
                </div>
                <div className="contestant-arrow">›</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderContestantDetail = () => {
    if (!activeContestant) return null;
    return (
      <div className="detail-container">
        <div className="top-bar">
          <button className="back-link" onClick={handleBackToList}>
            ← Back to Rankings
          </button>
          <div className="current-judge-label">
            Current Judge:&nbsp;
            <span>{selectedJudge}</span>
          </div>
        </div>

        <h1>Scorecard</h1>
        <div className="detail-card">
          <div className="detail-artist-label">Artist Name</div>
          <div className="detail-artist-name">{activeContestant}</div>

          {CRITERIA.map((crit) => (
            <div key={crit.key} className="criterion-block">
              <div className="criterion-header">
                <div className="criterion-title">{crit.label}</div>
                <div className="criterion-description">
                  {crit.description}
                </div>
              </div>
              <div className="criterion-scores-label">Scores: 1 2 3 4 5</div>
              <div className="score-buttons-row">
                {[1, 2, 3, 4, 5].map((num) => {
                  const selected = localScores[crit.key] === num;
                  return (
                    <button
                      key={num}
                      className={
                        selected
                          ? 'score-button score-button-selected'
                          : 'score-button'
                      }
                      onClick={() => handleScoreChange(crit.key, num)}
                    >
                      {num}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="comments-block">
            <label className="comments-label" htmlFor="comments">
              Comments
            </label>
            <textarea
              id="comments"
              className="comments-textarea"
              placeholder="Write any feedback about this performance…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>

          <button
            className="save-button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save & Return to Rankings'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="homepage-container">
      {view === 'selectJudge' && renderJudgeSelect()}
      {view === 'list' && selectedJudge && renderContestantList()}
      {view === 'detail' && selectedJudge && renderContestantDetail()}

      <style>{`
  :root{
    --brand-top:#fe24ad;
    --brand-bot:#a410e8;
    --brand-grad:linear-gradient(135deg,var(--brand-top),var(--brand-bot));
    --brand-top-20:rgba(254,36,173,.2);
    --brand-bot-40:rgba(164,16,232,.4);
  }

  *{margin:0;padding:0;box-sizing:border-box;}
  html,body{
    width:100%;
    min-height:100vh;
    overflow-x:hidden;
    overflow-y:auto;
    background:#000;
    font-family:sans-serif;
    color:#fff;
  }
  ::-webkit-scrollbar{width:8px;}
  ::-webkit-scrollbar-track{background:#050008;}
  ::-webkit-scrollbar-thumb{
    background:var(--brand-grad);
    border-radius:4px;box-shadow:0 0 8px rgba(254,36,173,.3);
  }

  .homepage-container{
    width:100%;
    min-height:100vh;
    background:
      radial-gradient(800px 600px at 15% 10%, var(--brand-top-20), transparent 60%),
      radial-gradient(700px 500px at 85% 90%, var(--brand-bot-40), transparent 60%),
      #000;
    color:#fff;
    padding:2rem;
    display:flex;
    flex-direction:column;
    animation:fadeInPage 1s ease-in-out;
    border-radius:0;
    padding-bottom:3.5rem;
  }
  @keyframes fadeInPage{from{opacity:0;}to{opacity:1;}}

  h1{
    text-align:center;margin-bottom:1rem;font-size:2.2rem;font-weight:800;
    background:var(--brand-grad);
    -webkit-background-clip:text;background-clip:text;
    -webkit-text-fill-color:transparent;
    text-shadow:0 0 18px rgba(164,16,232,.25);
  }

  p{text-align:center;color:rgba(255,255,255,.8);}

  .judge-select-container{
    max-width:900px;margin:0 auto;display:flex;flex-direction:column;align-items:center;
  }

  .judge-select-subtitle{
    margin-bottom:1.5rem;font-size:.95rem;max-width:540px;
  }

  .judge-cards{
    display:grid;grid-template-columns:repeat(3,minmax(0,1fr));
    gap:1rem;width:100%;margin-bottom:1.5rem;
  }

  .judge-card{
    background:#050008;
    border-radius:12px;
    border:1px solid var(--brand-bot-40);
    padding:1rem 1.2rem;
    display:flex;flex-direction:column;align-items:flex-start;
    cursor:pointer;
    box-shadow:0 2px 8px rgba(0,0,0,.55);
    transition:transform .2s ease, box-shadow .2s ease, border-color .2s ease;
  }

  .judge-card:hover{
    transform:translateY(-3px);
    box-shadow:0 4px 14px rgba(164,16,232,.4);
    border-color:var(--brand-top);
  }

  .judge-card-name{
    font-weight:700;
    font-size:1.05rem;
    background:var(--brand-grad);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
    margin-bottom:.25rem;
  }

  .judge-card-tag{
    font-size:.8rem;
    color:rgba(255,255,255,.7);
  }

  .judge-warning{
    font-size:.8rem;
    color:rgba(255,255,255,.6);
  }

  .contestant-list-container{
    max-width:900px;margin:0 auto;
  }

  .top-bar{
    display:flex;
    align-items:center;
    justify-content:space-between;
    margin-bottom:.75rem;
    column-gap:1rem;
    row-gap:.25rem;
    flex-wrap:wrap;
  }

  .back-link{
    background:transparent;
    border:none;
    color:rgba(255,255,255,.7);
    font-size:.85rem;
    cursor:pointer;
    padding:0;
    text-decoration:underline;
    text-decoration-color:var(--brand-top);
    text-underline-offset:3px;
    margin-right:.5rem;
  }

  .back-link:hover{
    color:#fff;
  }

  .current-judge-label{
    font-size:.85rem;
    color:rgba(255,255,255,.7);
  }

  .current-judge-label span{
    background:var(--brand-grad);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
    font-weight:600;
  }

  .loading-text{
    text-align:center;
    margin-top:2rem;
    font-size:.95rem;
    color:rgba(255,255,255,.7);
  }

  .contestant-list{
    margin-top:1rem;
    display:flex;
    flex-direction:column;
    gap:.6rem;
  }

  .contestant-row{
    width:100%;
    background:#050008;
    border-radius:10px;
    border:1px solid rgba(255,255,255,.08);
    padding:.8rem 1rem;
    display:flex;
    align-items:center;
    cursor:pointer;
    box-shadow:0 2px 6px rgba(0,0,0,.5);
    transition:transform .15s ease, box-shadow .15s ease, border-color .15s ease, background .15s ease;
  }

  .contestant-row:hover{
    transform:translateY(-2px);
    box-shadow:0 4px 12px rgba(164,16,232,.35);
    border-color:var(--brand-bot-40);
    background:radial-gradient(400px 300px at 10% 0%, rgba(254,36,173,.12), #050008);
  }

  .contestant-rank{
    width:28px;height:28px;
    border-radius:8px;
    border:1px solid var(--brand-bot-40);
    display:flex;align-items:center;justify-content:center;
    font-size:.85rem;font-weight:700;
    margin-right:.8rem;
    background:radial-gradient(circle at 0% 0%, rgba(254,36,173,.4), rgba(0,0,0,.9));
  }

  .contestant-main{
    flex:1;display:flex;flex-direction:column;align-items:flex-start;
  }

  .contestant-name{
    font-weight:700;
    font-size:1rem;
    color:#ffffff;
  }

  .contestant-sub{
    margin-top:.2rem;
    font-size:.8rem;
    color:rgba(255,255,255,.7);
    display:flex;flex-wrap:wrap;gap:.3rem;
  }

  .dot-separator{
    opacity:.7;
  }

  .contestant-arrow{
    font-size:1.2rem;
    color:rgba(255,255,255,.4);
    margin-left:.4rem;
  }

  .detail-container{
    max-width:900px;
    margin:0 auto 1.5rem auto;
  }

  .detail-card{
    margin-top:.75rem;
    background:#050008;
    border-radius:14px;
    border:1px solid var(--brand-bot-40);
    padding:1.2rem 1.2rem 1.4rem;
    box-shadow:0 2px 10px rgba(0,0,0,.7);
  }

  .detail-artist-label{
    font-size:.8rem;
    color:rgba(255,255,255,.6);
    margin-bottom:.2rem;
  }

  .detail-artist-name{
    font-size:1.1rem;
    font-weight:700;
    margin-bottom:1rem;
    color:#ffffff;
  }

  .criterion-block{
    margin-bottom:1rem;
    padding-bottom:.9rem;
    border-bottom:1px solid rgba(255,255,255,.08);
  }

  .criterion-header{
    display:flex;flex-direction:column;margin-bottom:.35rem;
  }

  .criterion-title{
    font-weight:700;
    font-size:.95rem;
  }

  .criterion-description{
    font-size:.8rem;
    color:rgba(255,255,255,.65);
  }

  .criterion-scores-label{
    font-size:.75rem;
    color:rgba(255,255,255,.6);
    margin-bottom:.3rem;
  }

  .score-buttons-row{
    display:flex;gap:.5rem;
  }

  .score-button{
    width:40px;height:40px;
    border-radius:10px;
    border:1px solid rgba(255,255,255,.3);
    background:rgba(0,0,0,.8);
    color:#fff;
    font-weight:700;
    cursor:pointer;
    font-size:.95rem;
    display:flex;align-items:center;justify-content:center;
    transition:transform .15s ease, box-shadow .15s ease, background .15s ease, border-color .15s ease;
  }

  .score-button-selected{
    border-color:var(--brand-top);
    background:var(--brand-grad);
    box-shadow:0 0 10px rgba(254,36,173,.6);
    transform:translateY(-1px);
  }

  .score-button:hover{
    transform:translateY(-1px);
    box-shadow:0 0 8px rgba(164,16,232,.5);
  }

  .comments-block{
    margin-top:.8rem;margin-bottom:1rem;
  }

  .comments-label{
    display:block;
    font-size:.85rem;
    margin-bottom:.3rem;
  }

  .comments-textarea{
    width:100%;
    min-height:90px;
    border-radius:10px;
    border:1px solid rgba(255,255,255,.2);
    padding:.6rem .7rem;
    background:#020006;
    color:#fff;
    font-size:1rem; /* prevent mobile zoom (≈16px) */
    resize:vertical;
    outline:none;
  }


  .comments-textarea:focus{
    border-color:var(--brand-top);
    box-shadow:0 0 10px rgba(164,16,232,.5);
  }

  .save-button{
    width:100%;
    margin-top:.3rem;
    padding:.8rem 1rem;
    border-radius:999px;
    border:none;
    background:var(--brand-grad);
    box-shadow:0 3px 12px rgba(164,16,232,.6);
    color:#fff;
    font-weight:700;
    font-size:.95rem;
    cursor:pointer;
    transition:transform .15s ease, box-shadow .15s ease, opacity .15s ease;
  }

  .save-button:hover{
    transform:translateY(-2px);
    box-shadow:0 5px 18px rgba(164,16,232,.8);
  }

  .save-button:disabled{
    opacity:.6;
    cursor:default;
    transform:none;
    box-shadow:0 3px 12px rgba(0,0,0,.8);
  }

  @media (max-width:900px){
    .judge-cards{
      grid-template-columns:1fr;
    }
  }

  @media (max-width:600px){
    .homepage-container{padding:1.5rem 1rem 3.5rem 1rem;}
    h1{font-size:2rem;}
    .contestant-row{padding:.7rem .85rem;}
    .score-button{width:38px;height:38px;}
  }
      `}</style>
    </div>
  );
};

export default ShowcaseContest;
